// The Top 8 drag contract, in a component-free module (react-refresh keeps the card component-only).
//
// Two payloads land on one drop handler:
//   friend:<friendBandId>  — dragged from a Friends row  → PIN that band into the target slot
//   top8:<index>           — dragged from a filled tile  → SWAP the two slots
//
// Every tile is a drop target, filled or empty; only filled tiles are drag sources.

import type { Top8Slot, Top8SlotWrite } from "../../app/hooks/useTop8.js";
import { TOP8_MAX } from "../../app/hooks/useTop8.js";

/** The DataTransfer type marking an in-page Top 8 drag. Readable during `dragover`, unlike the payload. */
export const TOP8_DRAG_TYPE = "application/x-cntrl-top8";

/**
 * A second, VALUELESS type stamped only on FRIEND drags, so the drop target can pick the matching
 * `dropEffect` during `dragover` (when payload VALUES are unreadable — only types are). This is
 * load-bearing, not cosmetic: Chromium cancels a drop whose final dropEffect is not permitted by the
 * source's `effectAllowed` — a friend drag (`effectAllowed: "copy"`) answered with `dropEffect: "move"`
 * never fires `drop` at all. The browser enforces this; jsdom does not, so no unit test can catch it —
 * a real-browser drag is the only proof (found live, 2026-08-20).
 */
export const TOP8_FRIEND_DRAG_TYPE = "application/x-cntrl-top8-friend";

/** A fixed-length grid: index = position - 1, `null` = an empty slot. */
export type Top8Grid = (Top8Slot | null)[];

export type Top8DragPayload =
  | { kind: "friend"; band: Top8Slot }
  | { kind: "slot"; index: number };

/**
 * A friend drag carries the whole band, not just its id — the drop target can then paint the tile without
 * a second lookup against the friends list.
 */
export function encodeDrag(payload: Top8DragPayload): string {
  return payload.kind === "friend" ? `friend:${JSON.stringify(payload.band)}` : `top8:${payload.index}`;
}

export function decodeDrag(raw: string | null | undefined): Top8DragPayload | null {
  if (!raw) return null;
  if (raw.startsWith("friend:")) {
    try {
      const band = JSON.parse(raw.slice("friend:".length)) as Top8Slot;
      return band?.friendBandId ? { kind: "friend", band } : null;
    } catch {
      return null;
    }
  }
  if (raw.startsWith("top8:")) {
    const index = Number(raw.slice("top8:".length));
    return Number.isInteger(index) && index >= 0 && index < TOP8_MAX ? { kind: "slot", index } : null;
  }
  return null;
}

/** The server grid (sparse, 1-based positions) projected onto the fixed 8-slot array the UI draws. */
export function gridFromSlots(slots: readonly Top8Slot[] | null | undefined): Top8Grid {
  const grid: Top8Grid = Array.from({ length: TOP8_MAX }, () => null);
  for (const slot of slots ?? []) {
    const index = (slot.position ?? 0) - 1;
    if (index >= 0 && index < TOP8_MAX) grid[index] = slot;
  }
  return grid;
}

/** The grid as a full-state PUT body — empty slots simply aren't sent (positions are sparse by design). */
export function gridToWrite(grid: Top8Grid): Top8SlotWrite[] {
  return grid.flatMap((slot, index) =>
    slot?.friendBandId ? [{ position: index + 1, friendBandId: slot.friendBandId }] : [],
  );
}

/**
 * Pin a band into a slot. If that band already sits in another slot, the old slot is VACATED first —
 * otherwise the same band shows up twice in the grid.
 */
export function pinToSlot(grid: Top8Grid, index: number, band: Top8Slot): Top8Grid {
  if (index < 0 || index >= TOP8_MAX) return grid;
  const next = grid.map((slot) => (slot?.friendBandId === band.friendBandId ? null : slot));
  next[index] = { ...band, position: index + 1 };
  return next;
}

/** Swap two slots. Swapping a filled tile onto an empty one is a move. */
export function swapSlots(grid: Top8Grid, from: number, to: number): Top8Grid {
  if (from === to || from < 0 || to < 0 || from >= TOP8_MAX || to >= TOP8_MAX) return grid;
  const next = [...grid];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Empty one slot (the tile's × affordance). */
export function vacateSlot(grid: Top8Grid, index: number): Top8Grid {
  if (index < 0 || index >= TOP8_MAX) return grid;
  const next = [...grid];
  next[index] = null;
  return next;
}

/** Whether two grids hold the same bands in the same positions (no write worth sending). */
export function sameGrid(a: Top8Grid, b: Top8Grid): boolean {
  return a.every((slot, i) => (slot?.friendBandId ?? null) === (b[i]?.friendBandId ?? null));
}
